import { useNavigate, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useMemo } from "react";
import { Shell } from "@/components/Shell";
import { Button } from "@/components/Button";
import { api } from "@/lib/api-client";

// Map raw wizard step IDs to friendlier customer-facing labels.
const STEP_LABELS: Record<string, string> = {
  palette: "Mixing your color palette",
  products: "Drafting sample products",
  categories: "Organizing categories",
  blog: "Writing blog posts",
  pages: "Building page content",
  logo: "Designing your logo",
  favicon: "Generating favicon",
  brandSymbol: "Picking a brand symbol",
  ogImage: "Creating social image",
  productImages: "Generating product images",
  blogImages: "Generating blog images",
  homepageImages: "Designing homepage imagery",
  policies: "Drafting policy pages",
  axiss: "Wiring up support",
  axissContent: "Seeding the knowledge base",
  voiceNumber: "Getting your phone number",
  taxEngine: "Setting up tax handling",
  turnstile: "Configuring spam protection",
  configFiles: "Writing site config",
  registry: "Registering your site",
  database: "Creating your database",
  minio: "Uploading images",
  seed: "Seeding your content",
  penny: "Activating fundraising tools",
  search: "Initializing search",
  resendDomain: "Setting up email",
  welcomeEmail: "Sending your welcome email",
  deploy: "Deploying — this is the long one",
  healthCheck: "Registering health monitoring",
  ownersbox: "Linking to Ownersbox",
  localProject: "Bundling local project files",
};

function friendlyLabel(step: string): string {
  return STEP_LABELS[step] ?? step;
}

export function Provisioning() {
  const { jobId = "" } = useParams<{ jobId: string }>();
  const navigate = useNavigate();

  const { data, isError, refetch } = useQuery({
    queryKey: ["provisioning", jobId],
    queryFn: () => api.provisioningStatus(jobId),
    refetchInterval: (q) => {
      const s = q.state.data?.status;
      return s === "completed" || s === "failed" ? false : 3000;
    },
    enabled: !!jobId,
  });

  // Once the wizard finishes successfully, send the customer to the dashboard.
  useEffect(() => {
    if (data?.status === "completed") {
      const t = setTimeout(() => navigate(`/dashboard?site=${data.result?.siteId ?? ""}`), 600);
      return () => clearTimeout(t);
    }
  }, [data, navigate]);

  const counts = useMemo(() => {
    if (!data) return { done: 0, total: 0 };
    return {
      done: data.progress.filter((p) => p.status === "done").length,
      total: data.progress.length,
    };
  }, [data]);

  if (isError) {
    return (
      <Shell title="Hmm, couldn't read status">
        <p className="mb-4 text-sm" style={{ color: "var(--color-muted)" }}>
          Something went wrong polling your site status.
        </p>
        <Button onClick={() => refetch()}>Try again</Button>
      </Shell>
    );
  }

  if (!data) {
    return (
      <Shell title="Starting up…">
        <p className="text-sm" style={{ color: "var(--color-muted)" }}>Loading status…</p>
      </Shell>
    );
  }

  const failed = data.status === "failed";
  const pct = counts.total === 0 ? 0 : Math.round((counts.done / counts.total) * 100);

  return (
    <Shell
      title={failed ? "Site creation hit a snag" : "Building your site"}
      subtitle={
        failed
          ? "We can usually pick up where we left off — your work isn't lost."
          : `Step ${counts.done} of ${counts.total} — typically takes 8–10 minutes.`
      }
    >
      <div
        className="mb-6 h-2 w-full overflow-hidden rounded-full"
        style={{ background: "var(--color-border)" }}
      >
        <div
          className="h-full transition-all"
          style={{ width: `${pct}%`, background: "var(--color-primary)" }}
        />
      </div>

      <ol className="space-y-2 text-sm">
        {data.progress.map((p) => (
          <li key={p.step} className="flex items-center gap-2">
            <StatusIcon status={p.status} />
            <span style={{ color: p.status === "done" ? "var(--color-muted)" : "var(--color-fg)" }}>
              {friendlyLabel(p.step)}
            </span>
          </li>
        ))}
      </ol>

      {failed && (
        <div className="mt-6 flex items-center gap-3">
          <Button
            onClick={async () => {
              await api.retry(jobId);
              refetch();
            }}
          >
            Retry
          </Button>
          {data.error && (
            <p className="text-xs" style={{ color: "var(--color-muted)" }}>
              {data.error.slice(0, 200)}
            </p>
          )}
        </div>
      )}
    </Shell>
  );
}

function StatusIcon({ status }: { status: string }) {
  if (status === "done") return <span style={{ color: "var(--color-primary)" }}>✓</span>;
  if (status === "running")
    return <span className="animate-pulse" style={{ color: "var(--color-accent)" }}>●</span>;
  if (status === "failed") return <span style={{ color: "#dc2626" }}>✗</span>;
  return <span style={{ color: "var(--color-border)" }}>○</span>;
}
